/**
 * AccountDetailPanel.jsx - one Account (or Site) in full: header KPIs with
 * the Designation and Status pills, and the Lead / Contacts / Opportunities /
 * Activity tabs (A-10: "Relationship" became "Lead"; Activity stays its own
 * tab because it continues after the account becomes a customer).
 *
 *   - Lead: the Lead card (stage + history, Designation, Status, owner, lead
 *     source, NDA, sites), the Tasks panel, and the Account Details card
 *     (everything else about the company or plant - AccountDetailsCard).
 *   - Contacts: primary first, anyone who left the company last with a grey
 *     tag (C-04). A name opens that contact's own page in place (C-01);
 *     Back returns here.
 *   - Opportunities: the Win / Loss chart, then the same table as the
 *     Opportunities view, pre-filtered to this account, with the same quick
 *     Won / Lost / Cancelled flow (confirm, and Won asks for the PO - Q-02).
 *   - Activity: the account timeline (AL-01 / AL-08) and the Log Activity
 *     form, both shared with the contact and opportunity pages. An open form
 *     stays mounted (hidden) while another tab is up, so its draft survives.
 *
 * Site rollup rule: a parent Account shows its own records PLUS every Site's,
 * each tagged with the site it came from - see server/routes/accounts.js.
 * Tasks are the exception (Paul Q12): they live on a site, so a company with
 * sites lists its sites' tasks but cannot add one of its own.
 */
import { useState, useMemo } from 'react';
import { Icon } from '../ui/Icon';
import { Pill } from '../ui/Pill';
import { Toast } from '../ui/Toast';
import { EditOpportunityModal } from '../../views/OpportunitiesView';
import { useCloseOpportunity } from '../opportunities/useCloseOpportunity';
import { useApp } from '../../context/AppContext';
import { OpportunitiesTable } from '../OpportunitiesTable';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import ActionsPanel from './ActionsPanel';
import RelationshipCard from './RelationshipCard';
import AccountDetailsCard from './AccountDetailsCard';
import ContactDetailPanel, { ContactFields, LEFT_TAG } from './ContactDetailPanel';
import Timeline from './Timeline';
import ActivityForm from './ActivityForm';
import {
  LEAD_STATUS_COLORS,
  LEFT_COMPANY,
  normalizeTier,
  designationOf,
  verticalOf,
  leadStatusOf,
  pickField,
  isLeftCompany,
} from '../../data/crmStages';
import OppProjectCard from './OppProjectCard';
import { BTN_PRIMARY, BTN_CANCEL, ERROR_LINE, tagStyle, designationTag, statusTag } from './crmStyles';
import { contactPayload, contactFormFrom, linkedinHref, sortContactsForList } from './crmFormHelpers';
import { computeAccountStats, matchCustomer } from '../../utils/customerMatch';
import NeedsDetailsChip from './NeedsDetailsChip';
import { NotTrackedChip } from './NotTrackedLead';

const tt = {
  fontFamily: 'Montserrat',
  fontSize: 11,
  borderRadius: 10,
  border: '1px solid #ECECEC',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  padding: '8px 12px',
};

const OppDetail = OppProjectCard;
const ProjectDetail = OppProjectCard;

const blankContact = () => ({
  name: '',
  role: '',
  contactRole: '',
  email: '',
  phone: '',
  linkedinUrl: '',
  isPrimary: false,
  contactStatus: 'Active',
});
const isPrimaryContact = (c) => !!pickField(c, 'isPrimary', 'is_primary');
const nameBtn = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'Montserrat',
  textAlign: 'left',
};

/* ===== Account Detail Panel ===== */
function AccountDetailPanel({ account, onBack, backLabel = 'Back to Accounts', editable, deletable, onOpenAccount }) {
  const { allProjects, allAccounts, updateAccount, updateProject, fetchAll } = useApp();
  // an opened opportunity, project or contact (C-01) replaces the panel until Back
  const [subView, setSubView] = useState(null);
  const [activeTab, setActiveTab] = useState('lead');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [contactForm, setContactForm] = useState(blankContact);
  const [contactErr, setContactErr] = useState('');
  const [toast, setToastState] = useState(null); // { message, tone }
  // tone 'error' shows a refusal in red; a plain message is a success
  const setToast = (message, tone) => setToastState(message ? { message, tone } : null);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactForm, setEditContactForm] = useState(blankContact);
  const [showOppForm, setShowOppForm] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  // bumped after a log is saved so the timeline re-reads
  const [timelineKey, setTimelineKey] = useState(0);
  // the quick Won / Lost / Cancelled buttons: confirm, and Won asks for the PO (Q-02)
  const closeFlow = useCloseOpportunity({ updateProject, onToast: (m) => setToast(m) });

  // Live-derived KPIs from allProjects — no more stale won_count / open_count.
  const liveStats = useMemo(() => {
    const stats = computeAccountStats(allAccounts, allProjects);
    return stats.get(account.id) || { won: 0, open: 0, lost: 0, cancelled: 0, wonValue: 0, pipelineValue: 0 };
  }, [allAccounts, allProjects, account.id]);
  const winRate = liveStats.won + liveStats.lost > 0 ? Math.round((liveStats.won / (liveStats.won + liveStats.lost)) * 100) : 0;

  // parent Account: its own opportunities plus every Site's (one rollup rule).
  // The table's Customer column already names the site, so rows self-label.
  // An opportunity linked to this record or one of its sites (projects.account_id,
  // A-02) counts even when its customer text does not match the name.
  const siteRows = useMemo(() => allAccounts.filter((a) => a.parentAccountId === account.id), [allAccounts, account.id]);
  const matchKeys = useMemo(
    () => [account.opportunityKey || account.name, ...siteRows.map((x) => x.opportunityKey || x.name)],
    [account, siteRows]
  );
  const familyIds = useMemo(() => new Set([account.id, ...siteRows.map((x) => x.id)]), [account.id, siteRows]);
  // One linked to ANOTHER known account is that account's, whatever its
  // customer text says - the same rule the counts follow (computeAccountStats)
  const knownIds = useMemo(() => new Set(allAccounts.map((a) => a.id)), [allAccounts]);
  const customerOpps = useMemo(
    () =>
      allProjects.filter((p) =>
        p.accountId && knownIds.has(p.accountId) ? familyIds.has(p.accountId) : matchKeys.some((k) => matchCustomer(k, p.customer))
      ),
    [allProjects, matchKeys, familyIds, knownIds]
  );

  const winLossData = [
    { name: 'Won', count: liveStats.won, fill: '#2E7D32' },
    { name: 'Open', count: liveStats.open, fill: '#1565C0' },
    { name: 'Lost', count: liveStats.lost, fill: '#C62828' },
  ].filter((d) => d.count > 0);

  if (subView?.type === 'opp') {
    const currentOpp = allProjects.find((o) => o.id === subView.item.id) || subView.item;
    return (
      <>
        {editingOpp && (
          <EditOpportunityModal
            key={editingOpp.id || 'new'}
            opp={editingOpp}
            onClose={() => setEditingOpp(null)}
            onSaved={() => setToast('Opportunity updated')}
          />
        )}
        <OppDetail
          opp={currentOpp}
          onBack={() => setSubView(null)}
          accountName={account.name}
          onEdit={editable ? (o) => setEditingOpp(o) : undefined}
        />
      </>
    );
  }
  if (subView?.type === 'project') {
    const currentProj = allProjects.find((o) => o.id === subView.item.id) || subView.item;
    return (
      <>
        {editingOpp && (
          <EditOpportunityModal
            key={editingOpp.id || 'new'}
            opp={editingOpp}
            onClose={() => setEditingOpp(null)}
            onSaved={() => setToast('Project updated')}
          />
        )}
        <ProjectDetail
          opp={currentProj}
          onBack={() => setSubView(null)}
          accountName={account.name}
          onEdit={editable ? (o) => setEditingOpp(o) : undefined}
        />
      </>
    );
  }
  // C-01: a contact's own page, in place. activeTab survives, so Back lands on the Contacts tab.
  if (subView?.type === 'contact') {
    return (
      <ContactDetailPanel
        contactId={String(subView.id)}
        onBack={() => setSubView(null)}
        backLabel={`Back to ${account.name}`}
        editable={editable}
        deletable={deletable}
        // this record's own name just comes back here; another account or site opens in its place
        onOpenAccount={(a) => {
          setSubView(null);
          if (a && a.id !== account.id && onOpenAccount) onOpenAccount(a);
        }}
      />
    );
  }
  const openContact = (c) => {
    if (c && c.id != null) setSubView({ type: 'contact', id: String(c.id) });
  };

  const isSite = !!account.parentAccountId;
  // X-07: a site starts on its company's vertical and may override it; the card offers "Same as company (...)"
  const companyVertical = isSite ? (allAccounts.find((a) => a.id === account.parentAccountId) || {}).vertical || null : null;
  // a company with sites is not a lead itself: no Status, and tasks go on its sites (Paul Q4 / Q12)
  const companyWithSites = !isSite && ((account.siteCount || 0) > 0 || siteRows.length > 0);
  // primary contact first (C-05), anyone who left the company last (C-04)
  const contacts = sortContactsForList(account.contacts);
  const readError = async (r, fallback) => {
    const b = await r.json().catch(() => ({}));
    return b.error || fallback;
  };

  // Contacts use their dedicated endpoints (POST/PUT/DELETE under
  // /api/accounts/:id/...). The old code PUT the whole account with a contacts
  // array, which the account-update route rejects (400) — so adds silently failed.
  // After each call we refresh; the parent re-derives the live account so the
  // new row shows immediately.
  const handleAddContact = async () => {
    if (!contactForm.name.trim()) {
      setContactErr("Enter the contact's name.");
      return;
    }
    try {
      const r = await fetch(`/api/accounts/${account.id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactPayload(contactForm)),
      });
      if (!r.ok) {
        setContactErr(await readError(r, 'Could not add the contact - try again.'));
        return;
      }
      await fetchAll();
    } catch (err) {
      console.error('addContact error:', err);
      setContactErr('Could not add the contact - try again.');
      return;
    }
    setContactForm(blankContact());
    setContactErr('');
    setShowContactForm(false);
    setToast('Contact added');
  };

  // C-02: the edit form also carries the account / site the contact belongs to;
  // picking another one moves the contact there (PUT account_id).
  const handleSaveEditContact = async () => {
    const c = contacts.find((x) => x.id === editingContactId);
    if (!c || !c.id) {
      setEditingContactId(null);
      return;
    }
    if (!editContactForm.name.trim()) {
      setContactErr("Enter the contact's name.");
      return;
    }
    const home = pickField(c, 'accountId', 'account_id') || account.id;
    const moveTo = editContactForm.accountId && editContactForm.accountId !== home ? editContactForm.accountId : null;
    try {
      const r = await fetch(`/api/accounts/${home}/contacts/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactPayload(editContactForm), ...(moveTo ? { account_id: moveTo } : {}) }),
      });
      if (!r.ok) {
        setContactErr(await readError(r, 'Could not save the contact - try again.'));
        return;
      }
      await fetchAll();
    } catch (err) {
      console.error('editContact error:', err);
      setContactErr('Could not save the contact - try again.');
      return;
    }
    setEditingContactId(null);
    setContactErr('');
    const dest = moveTo && allAccounts.find((a) => a.id === moveTo);
    setToast(dest ? `${editContactForm.name} moved to ${dest.name}` : 'Contact updated');
  };

  // a contact with tasks or activity on record is refused (409) - the server's
  // reason ("mark them Left company instead") shows in red
  const handleDeleteContact = async (c) => {
    if (!window.confirm('Delete this contact?')) return;
    if (!c || !c.id) return;
    try {
      const r = await fetch(`/api/accounts/${pickField(c, 'accountId', 'account_id') || account.id}/contacts/${c.id}`, {
        method: 'DELETE',
      });
      if (!r.ok) {
        setToast(await readError(r, 'Could not delete the contact.'), 'error');
        return;
      }
      await fetchAll();
    } catch (err) {
      console.error('deleteContact error:', err);
      setToast('Could not delete the contact.', 'error');
      return;
    }
    setToast('Contact deleted');
  };

  // Log Activity saved: re-read the timeline, and the store (the board, the meeting gate's view of it)
  const handleActivitySaved = (row) => {
    setShowActivityForm(false);
    setTimelineKey((k) => k + 1);
    fetchAll();
    setToast(row && (row.next_step || row.nextStep) ? 'Activity logged - next step added to Tasks' : 'Activity logged');
  };

  // A-10: the Relationship section is now the Lead tab; the tab key is the lower-cased label
  const TABS = ['Lead', 'Contacts', 'Opportunities', 'Activity'];
  const designation = designationOf(account);
  const leadStatus = leadStatusOf(account);
  const primaryLabel = isSite ? 'Primary contact for this site' : 'Primary contact for this account';

  return (
    <div>
      {toast && <Toast message={toast.message} tone={toast.tone} onDone={() => setToast(null)} />}
      {showOppForm && (
        <EditOpportunityModal
          // pre-link a site or a site-less account; a company with sites leaves it blank so the user picks the plant
          opp={{ customer: account.name, status: 'Open', accountId: companyWithSites ? '' : account.id }}
          onClose={() => setShowOppForm(false)}
          onSaved={() => setToast('Opportunity created')}
        />
      )}
      {/* keyed by record: another opportunity opened here gets a fresh editor, never this one's form */}
      {editingOpp && (
        <EditOpportunityModal
          key={editingOpp.id || 'new'}
          opp={editingOpp}
          onClose={() => setEditingOpp(null)}
          onSaved={() => setToast('Opportunity updated')}
        />
      )}
      {closeFlow.modals}

      <div
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, width: 'fit-content' }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        <div style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
          <Icon name="chevron" size={16} color="#555" />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{backLabel}</span>
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #ECECEC',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* HEADER */}
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #F0F0F0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: '#0D0D0D',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#43A047',
                  fontSize: 16,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {account.initials}
              </div>
              {account.isUserCreated && (
                <div
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#43A047',
                    border: '2px solid #fff',
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0D0D0D', marginBottom: 4 }}>{account.name}</div>
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#999', alignItems: 'center' }}>
                <span>{verticalOf(account) || account.industry}</span>
                <span style={designationTag(designation)} title="Designation">
                  {designation}
                </span>
                {!companyWithSites && (
                  <span style={statusTag(LEAD_STATUS_COLORS[leadStatus])} title="Status">
                    {leadStatus}
                  </span>
                )}
                {normalizeTier(account.tierEffective || account.tier) && (
                  <Pill
                    label={`Tier ${normalizeTier(account.tierEffective || account.tier)}${account.parentAccountId ? ' · inherited' : ''}`}
                  />
                )}
                {account.parentAccountId && (
                  <span style={{ fontSize: 9.5, color: '#7B1FA2', fontWeight: 700 }}>site of {account.parentName || 'parent account'}</span>
                )}
                {account.needsDetails && <NeedsDetailsChip />}
                <NotTrackedChip account={account} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#666' }}>
                {account.location && account.location !== '-' && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="mappin" size={11} color="#bbb" />
                    {account.location}
                  </span>
                )}
                {account.phone && account.phone !== '-' && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="phone" size={11} color="#bbb" />
                    {account.phone}
                  </span>
                )}
                {account.website && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="link" size={11} color="#bbb" />
                    {account.website}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div
            className="account-detail-header-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 20 }}
          >
            {[
              { label: 'Won', value: liveStats.won, color: '#1B5E20', bg: '#E8F5E9' },
              { label: 'Open', value: liveStats.open, color: '#1565C0', bg: '#E3F2FD' },
              { label: 'Lost', value: liveStats.lost, color: '#B71C1C', bg: '#FFEBEE' },
              { label: 'Win Rate', value: `${winRate}%`, color: '#0D0D0D', bg: '#F5F5F5' },
            ].map((s) => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: s.color,
                    marginTop: 6,
                    opacity: 0.7,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TAB BAR */}
        <div className="detail-tab-bar" style={{ display: 'flex', borderBottom: '1px solid #F0F0F0' }}>
          {TABS.map((t) => (
            <div
              key={t}
              onClick={() => setActiveTab(t.toLowerCase())}
              style={{
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                color: activeTab === t.toLowerCase() ? '#1B5E20' : '#aaa',
                borderBottom: `2px solid ${activeTab === t.toLowerCase() ? '#1B5E20' : 'transparent'}`,
              }}
            >
              {t}
            </div>
          ))}
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* LEAD (A-10): the lead journey, its next steps, then the company / plant details */}
          {activeTab === 'lead' && (
            <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* stage / designation / status / owner / lead source / NDA / sites */}
              <RelationshipCard
                key={account.id}
                account={account}
                sites={siteRows}
                editable={editable}
                onSave={(fields) => updateAccount(account.id, fields)}
                onOpenSite={(s) => onOpenAccount && onOpenAccount(s)}
                onCreateSite={async (f) => {
                  // Q8 / Q11: stage, lead source and a first task are required. No vertical: a
                  // new site starts on its company's and can override it later on its Account
                  // Details card (X-07); its tier is the company's.
                  try {
                    const r = await fetch('/api/accounts', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        name: f.name,
                        location: f.location || null,
                        parent_account_id: account.id,
                        stage: f.stage,
                        lead_source: f.lead_source,
                        lead_source_detail: f.lead_source_detail || null,
                        owner: f.owner || null,
                        industry: account.industry && account.industry !== '-' ? account.industry : null,
                        first_task: f.first_task,
                      }),
                    });
                    if (!r.ok) return { ok: false, error: await readError(r, 'Could not create the site - try again.') };
                    await fetchAll();
                    setToast(`Site "${f.name}" created`);
                    return { ok: true };
                  } catch (err) {
                    console.error('createSite error:', err);
                    return { ok: false, error: 'Could not create the site - try again.' };
                  }
                }}
              />
              <ActionsPanel
                entityType="account"
                entityId={account.id}
                entityLabel={account.name}
                editable={editable}
                includeSites={!isSite}
                allowAdd={!companyWithSites}
                onOpenContact={openContact}
              />
              {/* name, vertical, industry, tier / target score, phone, website, location, notes, robot brands, Details */}
              <AccountDetailsCard
                key={`details-${account.id}`}
                account={account}
                companyVertical={companyVertical}
                editable={editable}
                onSave={(fields) => updateAccount(account.id, fields)}
                onSaved={() => setToast('Account updated')}
              />
            </div>
          )}

          {/* CONTACTS - Title (the old role text), Role (C-03), Contact status (C-04), LinkedIn + Primary (C-05), Move to... (C-02) */}
          {activeTab === 'contacts' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>Contacts ({contacts.length})</div>
                {editable && (
                  <button
                    onClick={() => {
                      setShowContactForm(!showContactForm);
                      setContactErr('');
                    }}
                    style={{ ...BTN_PRIMARY, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    <Icon name="plus" size={11} color="#fff" /> Add Contact
                  </button>
                )}
              </div>
              {showContactForm && (
                <div style={{ background: '#FAFBFC', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #F0F0F0' }}>
                  <ContactFields
                    form={contactForm}
                    setForm={setContactForm}
                    primaryLabel={primaryLabel}
                    onEdited={() => setContactErr('')}
                  />
                  {contactErr && editingContactId == null && <div style={{ ...ERROR_LINE, marginTop: 10 }}>{contactErr}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button onClick={handleAddContact} style={BTN_PRIMARY}>
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowContactForm(false);
                        setContactErr('');
                      }}
                      style={BTN_CANCEL}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {contacts.length > 0 ? (
                contacts.map((c, i) => {
                  const siteName = c.siteName || c.site_name;
                  const siteId = c.siteId || c.site_id;
                  const contactRole = pickField(c, 'contactRole', 'contact_role');
                  const linkedin = pickField(c, 'linkedinUrl', 'linkedin_url');
                  const title = c.role && c.role !== '-' ? c.role : '';
                  const left = isLeftCompany(c);
                  return (
                    <div
                      key={c.id || i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '14px 16px',
                        borderRadius: 10,
                        border: '1px solid #F0F0F0',
                        background: left ? '#FAFAFA' : '#fff',
                        marginBottom: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      {editingContactId && editingContactId === c.id ? (
                        <div style={{ flex: 1 }}>
                          <ContactFields
                            form={editContactForm}
                            setForm={setEditContactForm}
                            accounts={allAccounts}
                            picker={{ label: 'Account / site (Move to...)', placeholder: 'Move to...', first: false }}
                            primaryLabel={primaryLabel}
                            onEdited={() => setContactErr('')}
                          />
                          {contactErr && <div style={{ ...ERROR_LINE, marginTop: 10 }}>{contactErr}</div>}
                          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                            <button onClick={handleSaveEditContact} style={BTN_PRIMARY}>
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingContactId(null);
                                setContactErr('');
                              }}
                              style={BTN_CANCEL}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 8,
                              background: '#F0F0F0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Icon name="user" size={16} color="#888" />
                          </div>
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#0D0D0D',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                              }}
                            >
                              {c.id != null ? (
                                <button
                                  onClick={() => openContact(c)}
                                  title={`Open ${c.name}`}
                                  style={{ ...nameBtn, color: left ? '#777' : '#0D0D0D' }}
                                >
                                  {c.name}
                                </button>
                              ) : (
                                c.name
                              )}
                              {isPrimaryContact(c) && (
                                <span style={tagStyle('#1B5E20', '#E8F5E9')} title={`Primary contact for ${siteName || account.name}`}>
                                  Primary
                                </span>
                              )}
                              {left && (
                                <span style={LEFT_TAG} title="Left the company - kept for history, hidden from task and attendee pickers">
                                  {LEFT_COMPANY}
                                </span>
                              )}
                              {siteName && <span style={tagStyle('#7B1FA2', '#F6EFFA')}>{siteName}</span>}
                              {!isSite && !siteName && companyWithSites && <span style={tagStyle('#555', '#F0F0F0')}>corporate</span>}
                            </div>
                            <div style={{ fontSize: 10, color: '#aaa', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {title && <span>{title}</span>}
                              {title && contactRole && <span>·</span>}
                              {contactRole && <span style={{ color: '#6A4C93', fontWeight: 700 }}>{contactRole}</span>}
                            </div>
                          </div>
                          {c.email && c.email !== '-' && <span style={{ fontSize: 10, color: '#1B5E20', fontWeight: 600 }}>{c.email}</span>}
                          {c.phone && c.phone !== '-' && <span style={{ fontSize: 10, color: '#666' }}>{c.phone}</span>}
                          {linkedin && (
                            <a
                              href={linkedinHref(linkedin)}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: 10, fontWeight: 700, color: '#0A66C2' }}
                            >
                              LinkedIn ↗
                            </a>
                          )}
                          {siteName ? (
                            <button
                              onClick={() => {
                                const st = allAccounts.find((a) => a.id === siteId);
                                if (st && onOpenAccount) onOpenAccount(st);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                fontSize: 9.5,
                                color: '#7B1FA2',
                                fontWeight: 700,
                                fontFamily: 'Montserrat',
                              }}
                            >
                              edit on the site ↗
                            </button>
                          ) : (
                            <>
                              {editable && (
                                <button
                                  title="Edit or move this contact"
                                  onClick={() => {
                                    setEditingContactId(c.id);
                                    setEditContactForm({
                                      ...contactFormFrom(c),
                                      accountId: pickField(c, 'accountId', 'account_id') || account.id,
                                    });
                                    setContactErr('');
                                  }}
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
                                >
                                  <Icon name="edit" size={13} color="#888" />
                                </button>
                              )}
                              {deletable && (
                                <button
                                  title="Delete this contact"
                                  onClick={() => handleDeleteContact(c)}
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
                                >
                                  <Icon name="trash" size={13} color="#B71C1C" />
                                </button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 11 }}>No contacts on file</div>
              )}
            </div>
          )}

          {/* OPPORTUNITIES - the Win / Loss chart, then the workspace-wide table pre-filtered to this account */}
          {activeTab === 'opportunities' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D', marginBottom: 10 }}>Win / Loss Overview</div>
                {winLossData.length > 0 ? (
                  <div style={{ height: 40 + winLossData.length * 34 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={winLossData} layout="vertical" barSize={22}>
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          tick={{ fontSize: 10, fill: '#aaa', fontFamily: 'Montserrat' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          dataKey="name"
                          type="category"
                          tick={{ fontSize: 11, fill: '#555', fontFamily: 'Montserrat', fontWeight: 700 }}
                          axisLine={false}
                          tickLine={false}
                          width={50}
                        />
                        <Tooltip contentStyle={tt} />
                        <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                          {winLossData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div style={{ color: '#bbb', fontSize: 11, padding: '10px 0' }}>No won, open or lost opportunities yet</div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>
                  Opportunities for {account.name} ({customerOpps.length})
                </div>
                {editable && (
                  <button
                    onClick={() => setShowOppForm(true)}
                    style={{ ...BTN_PRIMARY, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    <Icon name="plus" size={11} color="#fff" /> New Opportunity
                  </button>
                )}
              </div>
              <OpportunitiesTable
                opps={customerOpps}
                onEdit={(o) => setEditingOpp(o)}
                onMarkWon={closeFlow.markWon}
                onMarkLost={closeFlow.markLost}
                onMarkCancelled={closeFlow.markCancelled}
                onMarkNoQuote={closeFlow.markNoQuote}
                updateProject={updateProject}
                onToast={setToast}
                defaultStatus="All"
                title={`Opportunities · ${account.name}`}
              />
            </div>
          )}

          {/* ACTIVITY - one timeline (AL-01 / AL-08): logs, stage and status changes, task and opportunity events */}
          {activeTab === 'activity' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0D0D0D' }}>Activity Log</div>
              {/* gone while the form is open: it closes through its own Cancel / Close, so a partial save still refreshes */}
              {editable && !showActivityForm && (
                <button
                  onClick={() => setShowActivityForm(true)}
                  style={{ ...BTN_PRIMARY, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  <Icon name="plus" size={11} color="#fff" /> Log Activity
                </button>
              )}
            </div>
          )}
          {/* the form stays mounted, hidden, on the other tabs: a half-typed log (notes, attendees, tags, staged files) survives a look at Contacts */}
          {showActivityForm && (
            <div hidden={activeTab !== 'activity'}>
              <ActivityForm
                record={account}
                defaults={{ contact_ids: [], opportunity_ids: [] }}
                companyWithSites={companyWithSites}
                onSaved={handleActivitySaved}
                onCancel={() => setShowActivityForm(false)}
              />
            </div>
          )}
          {activeTab === 'activity' && (
            <Timeline
              kind="account"
              id={account.id}
              refreshKey={timelineKey}
              editable={editable}
              deletable={deletable}
              onChanged={() => fetchAll()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountDetailPanel;
